import { Widget, WidgetType } from 'features/chat/agent-chat-module/types/agent.service.type';
import type {
  ChatbotWidget,
  ChatbotWidgetDesignSettings,
  WidgetBannerSettings,
} from './types';

const chatbotWidgetDesignSettingsDefaultValue: ChatbotWidgetDesignSettings & WidgetBannerSettings = {
  headerStartColor: '#000000',
  headerEndColor: '#000000',
  titleColor: '#FFFFFF',
  chatBackgroundColor: '#FFFFFF',
  chatFontColor: '#000000',
  sendButtonBackgroundColor: '#000000',
  sendButtonFontColor: '#FFFFFF',
  borderColor: '#e5e7eb',
  'chat-toggle-button--background-color': '#000000',
  'chat-toggle-button--color': '#FFFFFF',
  banner: {
    enabled: false,
    imageUrl: '',
    imageId: '',
    text: '',
  },
};

export const ChatbotWidgetDefaultValue: ChatbotWidget = {
  name: '',
  logo_url: '',
  logo_id: null,
  greeting: '',
  designSettings: chatbotWidgetDesignSettingsDefaultValue,
  site_url: null,
  brand_color: null,
  allowed_domains: '',
  allow_visitor_tool_connect: true,
  host_cookie_name: '',
};

const formatAllowedDomains = (domains: string[] | undefined | null): string =>
  (domains ?? []).filter(Boolean).join(', ');

export const parseAllowedDomains = (value: string | undefined | null): string[] =>
  (value ?? '')
    .split(',')
    .map((domain) => domain.trim())
    .filter(Boolean);

export const parseChatbotWidgetData = (data: Widget | undefined): ChatbotWidget => {
  if (!data) return ChatbotWidgetDefaultValue;

  const designSettings = data.widget_settings?.find((item) => item.widget_type === WidgetType.CHAT);
  const allowedDomains = formatAllowedDomains(data.allowed_domains);
  const allowVisitorToolConnect =
    data.allow_visitor_tool_connect ?? data.AllowVisitorToolConnect ?? true;
  const hostCookieName = data.host_cookie_name ?? data.HostCookieName ?? '';

  if (!designSettings) {
    return {
      name: data.name || '',
      greeting: data.greeting || '',
      logo_url: data.logo_url || '',
      logo_id: data.logo_id || null,
      site_url: data.site_url || null,
      brand_color: data.brand_color || null,
      allowed_domains: allowedDomains,
      allow_visitor_tool_connect: allowVisitorToolConnect,
      host_cookie_name: hostCookieName,
      designSettings: chatbotWidgetDesignSettingsDefaultValue,
    };
  }

  if (!designSettings.fe_style) {
    return {
      name: data.name || '',
      greeting: data.greeting || '',
      logo_url: data.logo_url || '',
      logo_id: data.logo_id || null,
      site_url: data.site_url || null,
      brand_color: data.brand_color || null,
      allowed_domains: allowedDomains,
      allow_visitor_tool_connect: allowVisitorToolConnect,
      host_cookie_name: hostCookieName,
      designSettings: chatbotWidgetDesignSettingsDefaultValue,
    };
  }

  const parsedDesignSettings = JSON.parse(designSettings.fe_style);
  return {
    name: data.name || '',
    greeting: data.greeting || '',
    logo_url: data.logo_url || '',
    logo_id: data.logo_id || null,
    site_url: data.site_url || null,
    brand_color: data.brand_color || null,
    allowed_domains: allowedDomains,
    allow_visitor_tool_connect: allowVisitorToolConnect,
    host_cookie_name: hostCookieName,
    designSettings: { ...chatbotWidgetDesignSettingsDefaultValue, ...parsedDesignSettings },
  };
};
